import EmptyPlaceholder from "../../../controls/EmptyPlaceholder";
import FlexLayout from "../../../controls/FlexLayout";
import FormInput from "../../../controls/FormInput";
import Icon from "../../../controls/Icon";
import IconBar from "../../../controls/IconBar";
import { ToggleButton } from "../../../controls/TooltipControls";
import { ComponentEx, connect, translate } from "../../../controls/ComponentEx";
import opn from "../../../util/opn";
import { activeGameId } from "../../../util/selectors";
import { getSafe } from "../../../util/storeHelper";
import { truthy } from "../../../util/util";
import MainPage from "../../../views/MainPage";
import { nexusGameId } from "../../nexus_integration/util/convertGameId";
import { setPickerLayout, setSortManaged, setSortUnmanaged, } from "../actions/settings";
import GameRow from "./GameRow";
import GameThumbnail from "./GameThumbnail";
import ShowHiddenButton from "./ShowHiddenButton";
import { ratio } from "fuzzball";
import update from "immutability-helper";
import memoizeOne from "memoize-one";
import * as React from "react";
import { InputGroup, ListGroup, Panel, PanelGroup } from "react-bootstrap";
import Select from "react-select";
function gameFromDiscovery(id, discovered) {
    return {
        id,
        name: discovered.name ?? id,
        shortName: discovered.shortName,
        executable: discovered.executable,
        extensionPath: discovered.extensionPath,
        logo: discovered.logo,
        requiredFiles: [],
        supportedTools: [],
    };
}
function captureClick(evt) {
    evt.preventDefault();
}
function byGameName(lhs, rhs) {
    return lhs.name.localeCompare(rhs.name);
}
function nop() {
    // nop
}
/**
 * picker/configuration for game modes
 *
 * @class GamePicker
 */
class GamePicker extends ComponentEx {
    // "PAYDAY 2" vs "Payday 2" or "Resident Evil: Village" vs "Resident Evil Village" are 100 similar
    // "Final Fantasy 7 Remake" vs "Final Fantasy VII Remake" are 91 similar
    static SIMILARITY_RATIO = 90;
    buttons;
    mRef;
    mScrollRef;
    nexusGameById = memoizeOne((gameList) => gameList.reduce((prev, entry) => {
        prev[entry.domain_name] = entry;
        return prev;
    }, {}));
    nexusGameByName = memoizeOne((gameList) => gameList.reduce((prev, entry) => {
        prev[entry.name] = entry;
        return prev;
    }, {}));
    mNameLookup = {};
    constructor(props) {
        super(props);
        this.initState({
            showHidden: false,
            currentFilterValue: "",
            expandManaged: true,
            expandUnmanaged: true,
        });
        this.buttons = [
            {
                component: ShowHiddenButton,
                props: () => ({
                    t: this.props.t,
                    showHidden: this.state.showHidden,
                    toggleHidden: this.toggleHidden,
                }),
            },
        ];
    }
    render() {
        const { t, discoveredGames, extensions, extensionsInstalled, knownGames, pickerLayout, profiles, sortManaged, sortUnmanaged, } = this.props;
        const { showHidden, currentFilterValue, expandManaged, expandUnmanaged } = this.state;
        const installedExtIds = new Set(Object.values(extensionsInstalled).map((ext) => ext.modId));
        const installedNames = new Set(Object.values(extensionsInstalled).map((ext) => ext.name));
        // figuring out if a manually installed extension corresponds to a remotely available extension
        // isn't trivial, because the unique id and the game name stored in the extension list are both
        // assigned by us, when we compile it, there is no id in the original author-provided info.json
        // because we can't rely on authors to be consistent here.
        // Therefore we will also filter out based on game name, meaning there can only be one entry
        // for each game name, the one installed locally taking precedence.
        const installedGameNames = new Set(knownGames.map((game) => game.name.replace(/\t/g, " ")));
        // contains the extensions we don't have installed locally
        const extensionsUninstalled = extensions
            .filter((ext) => ext.type === "game")
            .filter((ext) => !installedExtIds.has(ext.modId) &&
            !installedNames.has(ext.name) &&
            !installedGameNames.has(ext.gameName));
        // TODO: lots of computation and it doesn't actually change except through discovery
        //   or when adding a profile
        const displayedGames = showHidden || !!currentFilterValue
            ? knownGames
            : knownGames.filter((game) => !(discoveredGames[game.id]?.hidden ?? false));
        const profileGames = new Set(Object.keys(profiles).map((profileId) => profiles[profileId].gameId));
        const managedGameList = [];
        const discoveredGameList = [];
        const supportedGameList = [];
        displayedGames.forEach((game) => {
            if (getSafe(discoveredGames, [game.id, "path"], undefined) !== undefined) {
                if (profileGames.has(game.id)) {
                    managedGameList.push(game);
                }
                else {
                    discoveredGameList.push(game);
                }
            }
            else {
                supportedGameList.push(game);
            }
        });
        supportedGameList.push(...extensionsUninstalled
            .map((ext) => ({
            id: ext.gameId || ext.name,
            name: ext.gameName || ext.name,
            extensionPath: undefined,
            imageURL: ext.image,
            requiredFiles: [],
            executable: undefined,
            contributed: ext.author,
        }))
            .filter((ext) => showHidden || !(discoveredGames[ext.id]?.hidden ?? false)));
        Object.keys(discoveredGames).forEach((gameId) => {
            if (knownGames.find((game) => game.id === gameId) === undefined) {
                if (discoveredGames[gameId].extensionPath === undefined) {
                    return;
                }
                if (profileGames.has(gameId)) {
                    managedGameList.push(gameFromDiscovery(gameId, discoveredGames[gameId]));
                }
                else {
                    discoveredGameList.push(gameFromDiscovery(gameId, discoveredGames[gameId]));
                }
            }
        });
        const unmanagedGameList = [].concat(discoveredGameList, supportedGameList);
        const filteredManaged = managedGameList
            .filter((game) => this.applyGameFilter(game))
            .sort(this.sortBy(sortManaged));
        const filteredUnmanaged = unmanagedGameList
            .filter((game) => this.applyGameFilter(game))
            .sort(this.sortBy(sortUnmanaged));
        const titleManaged = t("Managed ({{filterCount}})", {
            replace: {
                filterCount: this.getTabGameNumber(managedGameList, filteredManaged),
            },
        });
        const titleUnmanaged = t("Unmanaged ({{filterCount}})", {
            replace: {
                filterCount: this.getTabGameNumber(unmanagedGameList, filteredUnmanaged),
            },
        });
        return (React.createElement(MainPage, { domRef: this.setRef },
            React.createElement(MainPage.Header, null,
                React.createElement(IconBar, { group: "game-icons", staticElements: [], className: "menubar", t: t }),
                React.createElement("div", { className: "flex-fill" }),
                React.createElement(IconBar, { id: "gamepicker-layout-list", group: "gamepicker-layout-icons", staticElements: [], className: "menubar", t: t },
                    React.createElement(ShowHiddenButton, { t: this.props.t, showHidden: this.state.showHidden, toggleHidden: this.toggleHidden }),
                    React.createElement(ToggleButton, { id: "gamepicker-layout-list", onClick: this.setLayoutList, onIcon: "layout-list", offIcon: "layout-list", tooltip: t("List"), offTooltip: t("List"), state: pickerLayout === "list" },
                        React.createElement("span", { className: "button-text" }, t("List View"))),
                    React.createElement(ToggleButton, { id: "gamepicker-layout-grid", onClick: this.setLayoutSmall, onIcon: "layout-grid", offIcon: "layout-grid", tooltip: t("Grid"), offTooltip: t("Grid"), state: pickerLayout === "small" },
                        React.createElement("span", { className: "button-text" }, t("Grid View"))))),
            React.createElement(MainPage.Body, null,
                React.createElement(FlexLayout, { type: "column", className: "game-page" },
                    React.createElement(FlexLayout.Fixed, null,
                        React.createElement(Panel, { className: "game-filter-container" },
                            React.createElement(Panel.Body, null,
                                React.createElement(InputGroup, null,
                                    React.createElement(FormInput, { className: "game-filter-input", value: currentFilterValue, placeholder: t("Search for a game..."), onChange: this.onFilterInputChange, debounceTimer: 100, emptyIcon: "search", clearable: true }))))),
                    React.createElement(FlexLayout.Flex, null,
                        React.createElement("div", { ref: this.setScrollRef, className: "gamepicker-body" },
                            React.createElement(PanelGroup, { id: "game-panel-group" },
                                React.createElement(Panel, { expanded: expandManaged, eventKey: "managed", onToggle: nop },
                                    React.createElement(Panel.Heading, { onClick: this.toggleManaged },
                                        React.createElement(Icon, { name: expandManaged ? "showhide-down" : "showhide-right" }),
                                        React.createElement(Panel.Title, null, titleManaged),
                                        React.createElement("div", { className: "flex-fill" }),
                                        expandManaged ? (React.createElement("div", { className: "game-sort-container", onClick: captureClick },
                                            t("Sort by:"),
                                            React.createElement(Select, { className: "select-compact", options: [
                                                    { value: "alphabetical", label: t("Name A-Z") },
                                                    {
                                                        value: "recentlyused",
                                                        label: t("Recently used"),
                                                    },
                                                ], value: sortManaged, onChange: this.setSortManaged, clearable: false, autosize: false, searchable: false }))) : null),
                                    React.createElement(Panel.Body, { collapsible: true }, this.renderGames(filteredManaged, "managed"))),
                                React.createElement(Panel, { expanded: expandUnmanaged, eventKey: "unmanaged", onToggle: nop },
                                    React.createElement(Panel.Heading, { onClick: this.toggleUnmanaged },
                                        React.createElement(Icon, { name: expandUnmanaged ? "showhide-down" : "showhide-right" }),
                                        React.createElement(Panel.Title, null, titleUnmanaged),
                                        React.createElement("div", { className: "flex-fill" }),
                                        expandUnmanaged ? (React.createElement("div", { className: "game-sort-container", onClick: captureClick },
                                            t("Sort by:"),
                                            React.createElement(Select, { className: "select-compact", options: [
                                                    { value: "popular", label: t("Most Popular") },
                                                    { value: "alphabetical", label: t("Name A-Z") },
                                                    { value: "recent", label: t("Most Recent") },
                                                ], value: sortUnmanaged, onChange: this.setSortUnmanaged, clearable: false, autosize: false, searchable: false }))) : null),
                                    React.createElement(Panel.Body, { collapsible: true },
                                        this.renderGames(filteredUnmanaged, "unmanaged"),
                                        React.createElement(EmptyPlaceholder, { icon: "in-progress", text: t("Can't find the game you're looking for?"), subtext: React.createElement("a", { onClick: this.openGameExtWiki }, t("Your game may not be supported yet but adding it yourself " +
                                                "may be easier than you think. Click to learn how!")) }))))))))));
    }
    openGameExtWiki = () => {
        opn("https://github.com/Nexus-Mods/Vortex/wiki/MODDINGWIKI-Developers-General-Creating-a-game-extension").catch(() => null);
    };
    setSortManaged = (value) => {
        if (truthy(value)) {
            this.props.onSetSortManaged(value.value);
        }
    };
    setSortUnmanaged = (value) => {
        if (truthy(value)) {
            this.props.onSetSortUnmanaged(value.value);
        }
    };
    toggleManaged = (evt) => {
        if (!evt.isDefaultPrevented()) {
            this.nextState.expandManaged = !this.state.expandManaged;
        }
    };
    toggleUnmanaged = (evt) => {
        if (!evt.isDefaultPrevented()) {
            this.nextState.expandUnmanaged = !this.state.expandUnmanaged;
        }
    };
    onFilterInputChange = (input) => {
        this.nextState.currentFilterValue = input;
    };
    getTabGameNumber(unfiltered, filtered) {
        const { currentFilterValue } = this.state;
        return currentFilterValue
            ? `${filtered.length}/${unfiltered.length}`
            : `${unfiltered.length}`;
    }
    applyGameFilter = (game) => {
        const { currentFilterValue } = this.state;
        return (!currentFilterValue ||
            game.name?.toLowerCase().includes(currentFilterValue.toLowerCase()));
    };
    setRef = (ref) => {
        this.mRef = ref;
    };
    setScrollRef = (ref) => {
        this.mScrollRef = ref;
        this.forceUpdate();
    };
    getBounds = () => this.mRef.getBoundingClientRect();
    toggleHidden = () => {
        this.setState(update(this.state, { showHidden: { $set: !this.state.showHidden } }));
    };
    setLayoutList = () => {
        this.props.onSetPickerLayout("list");
    };
    setLayoutSmall = () => {
        this.props.onSetPickerLayout("small");
    };
    lastUsed(game) {
        const { profiles } = this.props;
        return Math.max(...Object.values(profiles)
            .filter((prof) => prof.gameId === game.id)
            .map((prof) => prof.lastActivated));
    }
    byRecentlyUsed = (lhs, rhs) => {
        return this.lastUsed(rhs) - this.lastUsed(lhs);
    };
    lookupName(input) {
        const { nexusGames } = this.props;
        if (this.mNameLookup[input] === undefined) {
            const exactMatch = nexusGames.find((i) => i.name === input);
            if (exactMatch !== undefined) {
                this.mNameLookup[input] = input;
            }
            else {
                const sorted = nexusGames
                    .map((item) => ({ item, ratio: ratio(item.name, input) }))
                    .filter((iter) => iter.ratio > GamePicker.SIMILARITY_RATIO)
                    .sort((lhs, rhs) => rhs.ratio - lhs.ratio);
                this.mNameLookup[input] =
                    sorted.length > 0 ? sorted[0].item.name : input;
            }
        }
        return this.mNameLookup[input];
    }
    identifyGame(game) {
        const { nexusGames } = this.props;
        return (this.nexusGameById(nexusGames)[nexusGameId(game)] ??
            this.nexusGameByName(nexusGames)[this.lookupName(game.name)]);
    }
    approvedTime(game) {
        const nexusGame = this.identifyGame(game);
        return nexusGame?.approved_date ?? 0;
    }
    byRecent = (lhs, rhs) => {
        return this.approvedTime(rhs) - this.approvedTime(lhs);
    };
    gameFileCount(game) {
        const nexusGame = this.identifyGame(game);
        return nexusGame?.downloads ?? 0;
    }
    byPopular = (lhs, rhs) => {
        return this.gameFileCount(rhs) - this.gameFileCount(lhs);
    };
    sortBy = (sortMode) => {
        return ({
            recentlyused: this.byRecentlyUsed,
            recent: this.byRecent,
            popular: this.byPopular,
        }[sortMode] ?? byGameName);
    };
    renderGames = (games, type) => {
        const { t, gameMode, pickerLayout } = this.props;
        const { currentFilterValue } = this.state;
        if (games.length === 0) {
            if (truthy(currentFilterValue)) {
                return null;
            }
            else if (type === "managed") {
                return (React.createElement(EmptyPlaceholder, { icon: "game", text: t("You haven't managed any games yet"), subtext: t('To start managing a game, go to "Unmanaged" and activate a game there.') }));
            }
        }
        switch (pickerLayout) {
            case "list":
                return this.renderGamesList(games, type, gameMode);
            case "small":
                return this.renderGamesSmall(games, type, gameMode);
            default:
                throw new Error("invalid picker layout " + pickerLayout);
        }
    };
    renderGamesList(games, type, gameMode) {
        const { t, discoveredGames, onRefreshGameInfo } = this.props;
        return (React.createElement(ListGroup, null, games.map((game) => (React.createElement(GameRow, { t: t, getBounds: this.getBounds, container: this.mScrollRef, key: game.id, game: game, discovery: discoveredGames[game.id], type: type, active: game.id === gameMode, onRefreshGameInfo: onRefreshGameInfo, onBrowseGameLocation: this.props.onBrowseGameLocation })))));
    }
    renderGamesSmall(games, type, gameMode) {
        const { t, discoveredGames, onRefreshGameInfo } = this.props;
        const isDiscovered = (gameId) => getSafe(discoveredGames, [gameId, "path"], undefined) !== undefined;
        return (React.createElement("div", null,
            React.createElement("div", { className: "game-group" }, games.map((game) => (React.createElement(GameThumbnail, { t: t, key: game.id + "_" + (game.contributed ?? "official"), game: game, type: type, active: game.id === gameMode, onRefreshGameInfo: onRefreshGameInfo, getBounds: this.getBounds, container: this.mScrollRef, discovered: isDiscovered(game.id) }))))));
    }
}
function mapStateToProps(state) {
    return {
        gameMode: activeGameId(state),
        discoveredGames: state.settings.gameMode.discovered,
        pickerLayout: state.settings.gameMode.pickerLayout || "list",
        profiles: state.persistent.profiles,
        knownGames: state.session.gameMode.known,
        extensions: state.session.extensions.available,
        extensionsInstalled: state.session.extensions.installed,
        sortManaged: state.settings.gameMode.sortManaged ?? "alphabetical",
        sortUnmanaged: state.settings.gameMode.sortUnmanaged ?? "alphabetical",
    };
}
function mapDispatchToProps(dispatch) {
    return {
        onSetPickerLayout: (layout) => dispatch(setPickerLayout(layout)),
        onSetSortManaged: (sorting) => dispatch(setSortManaged(sorting)),
        onSetSortUnmanaged: (sorting) => dispatch(setSortUnmanaged(sorting)),
    };
}
export default translate(["common"])(connect(mapStateToProps, mapDispatchToProps)(GamePicker));
